import { type ApiRequestService } from '@coris-ts/repository/data-source/api-request.service'
import { GetDashboardInteractor } from './domain/interactors/get-dashboard.interactor'
import { Cached } from '@coris-ts/helpers/utilities'
import { GetRepositoryMapper } from '@coris-ts/repository/repository-mapper'
import { provideDefaultNetworkDataSource } from '@coris-ts/repository/data-source/network.data-source'
import { SingleGetDataSourceRepository } from '@coris-ts/repository/single-data-source.repository'
import { GetInteractor } from '@coris-ts/repository/interactor/get.interactor'
import { UsersTotalsEntity, UsersMetricsEntity } from './data/entities/dashboard.entity'
import { MediaPageEntity, MediaTotalsEntity } from '../engagement/data/entities/media.entity'
import { EventsTotalsEntity } from '../engagement/data/entities/events.entity'
import { CommunityTotalsEntity } from '../engagement/data/entities/community.entity'
import { ChatTotalsEntity } from '../engagement/data/entities/chat.entity'

export interface DashboardComponent {
  provideGetDashboard: () => GetDashboardInteractor
}

export class DashboardDefaultModule implements DashboardComponent {
  constructor(private readonly _apiRequestService: ApiRequestService) {}

  @Cached()
  private getUsersTotalsRepository(): GetRepositoryMapper<UsersTotalsEntity, UsersTotalsEntity> {
    const dataSource = provideDefaultNetworkDataSource(this._apiRequestService, UsersTotalsEntity)
    const repository = new SingleGetDataSourceRepository(dataSource)
    return new GetRepositoryMapper(repository, { map: (entity: UsersTotalsEntity) => entity })
  }

  @Cached()
  private getUsersMetricsRepository(): GetRepositoryMapper<UsersMetricsEntity, UsersMetricsEntity> {
    const dataSource = provideDefaultNetworkDataSource(this._apiRequestService, UsersMetricsEntity)
    const repository = new SingleGetDataSourceRepository(dataSource)
    return new GetRepositoryMapper(repository, { map: (entity: UsersMetricsEntity) => entity })
  }

  @Cached()
  private getMediaTotalsRepository(): GetRepositoryMapper<MediaTotalsEntity, MediaTotalsEntity> {
    const dataSource = provideDefaultNetworkDataSource(this._apiRequestService, MediaTotalsEntity)
    const repository = new SingleGetDataSourceRepository(dataSource)
    return new GetRepositoryMapper(repository, { map: (entity: MediaTotalsEntity) => entity })
  }

  @Cached()
  private getMediaPageRepository(): GetRepositoryMapper<MediaPageEntity, MediaPageEntity> {
    const dataSource = provideDefaultNetworkDataSource(this._apiRequestService, MediaPageEntity)
    const repository = new SingleGetDataSourceRepository(dataSource)
    return new GetRepositoryMapper(repository, { map: (entity: MediaPageEntity) => entity })
  }

  @Cached()
  private getEventsTotalsRepository(): GetRepositoryMapper<EventsTotalsEntity, EventsTotalsEntity> {
    const dataSource = provideDefaultNetworkDataSource(this._apiRequestService, EventsTotalsEntity)
    const repository = new SingleGetDataSourceRepository(dataSource)
    return new GetRepositoryMapper(repository, { map: (entity: EventsTotalsEntity) => entity })
  }

  @Cached()
  private getCommunityTotalsRepository(): GetRepositoryMapper<CommunityTotalsEntity, CommunityTotalsEntity> {
    const dataSource = provideDefaultNetworkDataSource(this._apiRequestService, CommunityTotalsEntity)
    const repository = new SingleGetDataSourceRepository(dataSource)
    return new GetRepositoryMapper(repository, { map: (entity: CommunityTotalsEntity) => entity })
  }

  @Cached()
  private getChatTotalsRepository(): GetRepositoryMapper<ChatTotalsEntity, ChatTotalsEntity> {
    const dataSource = provideDefaultNetworkDataSource(this._apiRequestService, ChatTotalsEntity)
    const repository = new SingleGetDataSourceRepository(dataSource)
    return new GetRepositoryMapper(repository, { map: (entity: ChatTotalsEntity) => entity })
  }

  public provideGetDashboard(): GetDashboardInteractor {
    return new GetDashboardInteractor(
      new GetInteractor<UsersTotalsEntity>(this.getUsersTotalsRepository()),
      new GetInteractor<UsersMetricsEntity>(this.getUsersMetricsRepository()),
      new GetInteractor<MediaTotalsEntity>(this.getMediaTotalsRepository()),
      new GetInteractor<MediaPageEntity>(this.getMediaPageRepository()),
      new GetInteractor<EventsTotalsEntity>(this.getEventsTotalsRepository()),
      new GetInteractor<CommunityTotalsEntity>(this.getCommunityTotalsRepository()),
      new GetInteractor<ChatTotalsEntity>(this.getChatTotalsRepository())
    )
  }
}
